import { useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { resolveRichText } from '@vibesites/runtime';
import type { Faq } from '@vibesites/schema';
import {
  EditableCollectionBinding,
  useCollectionBinding,
  useVibesites,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

function hasValue(val: unknown): val is string {
  if (val == null) return false;
  const s = String(val).trim().toLowerCase();
  return s !== '' && s !== 'false' && s !== 'null' && s !== 'undefined';
}

function looksLikeHtml(value: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(value);
}

interface FaqGroup {
  category: string | null;
  items: Faq[];
}

function groupFaqs(faqs: Faq[]): FaqGroup[] {
  const order: string[] = [];
  const byCategory = new Map<string, Faq[]>();
  const uncategorized: Faq[] = [];

  for (const faq of faqs) {
    const category = hasValue(faq.category) ? faq.category.trim() : '';
    if (!category) {
      uncategorized.push(faq);
      continue;
    }
    if (!byCategory.has(category)) {
      order.push(category);
      byCategory.set(category, []);
    }
    byCategory.get(category)!.push(faq);
  }

  const groups: FaqGroup[] = order.map((category) => ({
    category,
    items: byCategory.get(category) ?? [],
  }));

  if (uncategorized.length > 0) {
    groups.push({ category: groups.length > 0 ? 'More' : null, items: uncategorized });
  }

  return groups;
}

function FaqAnswer({ html, property }: { html: string; property: Record<string, any> }) {
  const resolved = resolveRichText(html, property);
  if (!resolved) return null;

  if (!looksLikeHtml(resolved)) {
    return <p>{resolved}</p>;
  }

  return <div dangerouslySetInnerHTML={{ __html: resolved }} />;
}

function FaqItem({
  faq,
  property,
}: {
  faq: Faq;
  property: Record<string, any>;
}) {
  const question = hasValue(faq.question) ? faq.question : 'Question';

  return (
    <details className="faq-item">
      <summary className="faq-summary flex cursor-pointer list-none items-start justify-between gap-6 px-6 py-5 text-left sm:px-8 sm:py-6">
        <span className="font-heading text-base font-medium leading-snug text-[var(--vibe-color-text)] sm:text-lg">
          {question}
        </span>
        <span className="faq-icon mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center text-[var(--vibe-color-primary)]" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" className="h-5 w-5">
            <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </summary>
      <div className="faq-answer px-6 pb-6 font-body text-sm leading-relaxed text-[var(--vibe-color-text-muted)] sm:px-8 sm:text-base [&_a]:text-primary [&_a]:underline [&_li]:mb-1 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-3 [&_p:last-child]:mb-0 [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5">
        {hasValue(faq.answer) ? <FaqAnswer html={faq.answer} property={property} /> : null}
      </div>
    </details>
  );
}

export function FaqAccordion() {
  const { property } = useVibesites();
  const faqs = useCollectionBinding<Faq>('faqaccordion.items', {
    collection: 'faqs',
  });

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const groups = useMemo(() => groupFaqs(faqs ?? []), [faqs]);
  const hasFaqs = groups.some((group) => group.items.length > 0);
  const showCategoryHeadings = groups.length > 1;

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up bg-[var(--vibe-color-background)] py-12 md:py-20"
    >
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <EditableCollectionBinding bindingPath="faqaccordion.items" label="FAQs">
          {hasFaqs ? (
            <div>
              {groups.map((group) => (
                <div key={group.category ?? 'all'} className="mb-8 last:mb-0">
                  {showCategoryHeadings && group.category ? (
                    <h2 className="mb-4 font-heading text-lg font-normal uppercase tracking-wider text-[var(--vibe-color-primary)]">
                      {group.category}
                    </h2>
                  ) : null}
                  <div className="border border-[var(--vibe-color-border)] bg-[var(--vibe-color-surface)]">
                    {group.items.map((faq, index) => (
                      <FaqItem
                        key={faq.id || `${group.category ?? 'faq'}-${index}`}
                        faq={faq}
                        property={property as Record<string, any>}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="border border-[var(--vibe-color-border)] bg-[var(--vibe-color-surface)] px-6 py-12 text-center">
              <p className="font-body text-base text-[var(--vibe-color-text-muted)]">
                Frequently asked questions will appear here.
              </p>
            </div>
          )}
        </EditableCollectionBinding>
      </div>

      <style>{`
        .faq-item + .faq-item {
          border-top: 1px solid var(--vibe-color-border);
        }
        .faq-summary::-webkit-details-marker,
        .faq-summary::marker {
          display: none;
        }
        .faq-icon {
          transition: transform var(--vibe-transition-fast, 150ms ease);
        }
        .faq-item[open] .faq-icon {
          transform: rotate(180deg);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FaqAccordion',
  displayName: 'FAQ Accordion',
  tokens: {},
  collectionBindings: {
    'faqaccordion.items': {
      label: 'FAQs',
      collection: 'faqs',
      description: 'Questions and answers from the FAQ collection.',
      allowCustomItems: true,
    },
  },
};

(FaqAccordion as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FaqAccordion;
